import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldAlert, FileSearch } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => v === null || v === undefined ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const BookingPaymentAuditCard = ({ token }) => {
  const [bookingId, setBookingId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!bookingId.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/finance/booking-payment-audit/${encodeURIComponent(bookingId.trim())}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="booking-payment-audit-card" className="rounded-2xl border border-white/10 bg-[#0C0C0C] p-5 space-y-4">
      <div>
        <h3 className="font-display font-bold text-white flex items-center gap-2"><KeyRound size={18} className="text-[#F2B705]" /> Single-Booking Payment Audit — Exact ID Only</h3>
        <p className="text-white/45 text-sm mt-0.5">Read-only. Keyed strictly by booking_id — never by name — to avoid touching the wrong customer. Nothing is changed.</p>
      </div>

      <div className="flex gap-2">
        <input data-testid="booking-payment-audit-id-input" value={bookingId} onChange={(e) => setBookingId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()} placeholder="Exact booking_id…"
          className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white font-mono placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
        <button data-testid="booking-payment-audit-run-btn" onClick={run} disabled={busy || !bookingId.trim()}
          className="bg-[#F2B705] text-black font-bold px-5 rounded-lg hover:bg-white transition-colors disabled:opacity-60 flex items-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <FileSearch size={16} />} Audit
        </button>
      </div>

      {result && (
        <div className="space-y-4 border-t border-white/10 pt-4">
          <div className="text-white/80 text-sm">
            <span className="font-bold text-white">{result.name}</span> · {result.vehicle} · {result.pickup_date} → {result.return_date} ·{" "}
            <span className="text-white/50">{result.lifecycle_status} / {result.status}</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Total Charges</div>
              <div className="text-white/85 font-bold mt-1">{money(result.current.total_price)}</div>
            </div>
            <div className="bg-[#0A0A0A] border border-red-400/30 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Current (Stored) Paid</div>
              <div className="text-red-400 font-bold mt-1">{money(result.current.amount_paid)}</div>
            </div>
            <div className="bg-[#0A0A0A] border border-emerald-400/30 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Proposed (Canonical) Paid</div>
              <div className="text-emerald-400 font-bold mt-1">{money(result.proposed.amount_paid)}</div>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Difference</div>
              <div className="text-amber-400 font-bold mt-1">{money(result.amount_paid_difference)}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Current Displayed Balance</div>
              <div className="text-white/85 font-bold mt-1">{result.current.credit > 0 ? `Credit ${money(result.current.credit)}` : money(result.current.balance)}</div>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Proposed Balance</div>
              <div className="text-[#F2B705] font-bold mt-1">{result.proposed.credit > 0 ? `Credit ${money(result.proposed.credit)}` : money(result.proposed.balance)}</div>
            </div>
          </div>

          {result.duplicate_storms_flagged_for_this_booking?.length > 0 && (
            <div data-testid="booking-payment-audit-storms" className="rounded-xl border border-amber-400/30 bg-amber-400/[0.05] p-3 text-xs space-y-1">
              <div className="text-amber-400 font-semibold flex items-center gap-1.5"><ShieldAlert size={13} /> Duplicate storms flagged in this booking's ledger</div>
              {result.duplicate_storms_flagged_for_this_booking.map((s, i) => (
                <div key={i} className="text-white/70">{s.reason} — total excess {money(s.total_excess_to_remove)}</div>
              ))}
            </div>
          )}

          <div className="rounded-xl border border-white/10 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10">
                  <th className="px-3 py-2">Date</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Source ID</th>
                  <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Flag</th>
                </tr>
              </thead>
              <tbody>
                {result.ledger_rows.map((r) => (
                  <tr key={r.id} data-testid={`booking-payment-audit-ledger-row-${r.id}`} className="border-b border-white/5">
                    <td className="px-3 py-2 text-white/50">{(r.paid_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-white/70">{r.type}</td>
                    <td className="px-3 py-2 text-white/35 font-mono">{r.source_id || "—"}</td>
                    <td className="px-3 py-2 text-right text-white/80">{money(r.net_amount)}</td>
                    <td className="px-3 py-2">
                      {r.reversed ? <span className="text-white/30">reversed</span> :
                       r.duplicate_of_source_id_row ? <span className="text-red-400">duplicate source_id</span> : <span className="text-emerald-400/70">kept</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.extensions?.length > 0 && (
            <div className="rounded-xl border border-white/10 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10">
                    <th className="px-3 py-2">#</th><th className="px-3 py-2">Days</th><th className="px-3 py-2 text-right">Stored Charge</th>
                    <th className="px-3 py-2 text-right">Rate Used</th><th className="px-3 py-2 text-right">At Original Rate</th><th className="px-3 py-2 text-right">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {result.extensions.map((e) => (
                    <tr key={e.extension_number} data-testid={`booking-payment-audit-ext-${e.extension_number}`} className="border-b border-white/5">
                      <td className="px-3 py-2 text-white/70">#{e.extension_number}</td>
                      <td className="px-3 py-2 text-white/70">{e.days}</td>
                      <td className="px-3 py-2 text-right text-white/80">{money(e.stored_charge)}</td>
                      <td className="px-3 py-2 text-right text-white/50">{money(e.rate_used_per_day)}/day</td>
                      <td className="px-3 py-2 text-right text-[#F2B705]">{money(e.alt_charge_at_original_booking_rate)}</td>
                      <td className={`px-3 py-2 text-right font-bold ${e.diff_vs_original_rate ? "text-amber-400" : "text-white/30"}`}>{money(e.diff_vs_original_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[11px] text-white/35">{result.note}</p>
        </div>
      )}
    </div>
  );
};
